import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Archive, ArchiveRestore, ArrowDown, ArrowUp, FileDown, History, ListChecks, Loader2, Lock, MessageSquare,
  Plus, Redo2, Settings2, SlidersHorizontal, Sparkles, Trash2, Undo2, X, ZoomIn, ZoomOut,
} from "lucide-react";
import { reportApi } from "../../services/presentationApi";
import { openNexus, usePublishNexusPage } from "../../context/nexusPage";
import { useAuth } from "../Auth";
import { useVersionedDocument } from "../Offers/useVersionedDocument";
import { BackLink, SaveStatus, SyncBanners, TitleField, ToolbarButton } from "../Offers/EditorChrome";
import RevisionsPanel from "../Offers/RevisionsPanel";
import useClientShare from "../Offers/clientShare";
import CommentsPanel, { useProposalComments } from "../Offers/proposal/CommentsPanel";
import { diffProposal, mergeChanges } from "../Offers/proposal/changes";
import { DriveChip, GuidesPanel, PresentationSettings, useDriveStatus } from "../Presentations/PresentationPanels";
import ReportDocument, { PAGE } from "./ReportDocument";
import ReportInspector from "./ReportInspector";
import {
  addBlock, duplicateBlock, insertSection, moveBlock, moveSection, newSection, removeBlock, removeSection,
  sectionNumbers, updateBlock, updateMeta, updateSection,
} from "./reportOps";

const HIGHLIGHT_MS = 8000;
const SCOPE_NOUNS = { project: "Projet", client: "Client", programme: "Programme" };

// L'espace de travail prend la hauteur de la fenêtre sous l'en-tête : le plan, le rapport et le panneau défilent à part.
const useWorkspaceHeight = (ref) => {
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    const update = () => {
      if (!ref.current || !window.matchMedia("(min-width: 1280px)").matches) { setHeight(null); return; }
      const top = ref.current.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(480, Math.round(window.innerHeight - top - 16)));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  });
  return height;
};

// La page A4 (794 px) réduite à la largeur de la colonne centrale quand elle est plus étroite.
const usePaperScale = (ref) => {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setScale(Math.min(1, Math.max(0.4, (node.clientWidth - 32) / PAGE.width)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  });
  return scale;
};

// Paliers du zoom ; « Ajuster » revient à la largeur de la colonne.
const ZOOMS = [0.5, 0.67, 0.8, 1, 1.25, 1.5, 2];
const nextZoom = (scale, direction) => (direction > 0
  ? ZOOMS.find((value) => value > scale + 0.01) || ZOOMS[ZOOMS.length - 1]
  : [...ZOOMS].reverse().find((value) => value < scale - 0.01) || ZOOMS[0]);

const Check = ({ label, checked, onChange, disabled }) => (
  <label className="flex items-center gap-2 text-sm text-slate-700">
    <input type="checkbox" disabled={disabled} checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
    {label}
  </label>
);

// Éditeur d'un rapport : plan à gauche, le rapport tel qu'il sera imprimé (A4) au centre, éditable sur place, et le
// panneau de droite (commentaires, édition, versions, réglages, consignes). Même modèle que les présentations.
const ReportEditor = () => {
  const { reportId } = useParams();
  const id = Number(reportId);
  const [panel, setPanel] = useState("comments");
  const [selection, setSelection] = useState({ sectionId: null, blockId: null });
  const [exporting, setExporting] = useState(false);
  const [changes, setChanges] = useState({});
  const [notice, setNotice] = useState(null);
  const workspaceRef = useRef(null);
  const paperRef = useRef(null);
  const nodes = useRef({});
  const height = useWorkspaceHeight(workspaceRef);
  const fit = usePaperScale(paperRef);
  const [zoom, setZoom] = useState(null);
  const scale = zoom || fit;
  const doc = useVersionedDocument({ id, api: reportApi, docKey: "presentation", watchRevisions: panel === "revisions" });
  const {
    data, setData, loadError, shown, apply, undo, redo, canUndo, canRedo, setMessage, save, canEdit, archived,
    editable, viewing, viewRevision, restoreRevision, labelRevision, revisions, updateMeta: updateDocument, remoteChange,
  } = doc;
  const { user } = useAuth();
  const clientShare = useClientShare({ data, setData, user, refreshRevisions: doc.refreshRevisions, setMessage, revisions });
  const { comments, refresh: refreshComments } = useProposalComments(id, setMessage, reportApi);
  const drive = useDriveStatus(id, reportApi);

  useEffect(() => {
    if (!remoteChange) return;
    const found = diffProposal({ ...remoteChange.previous, slides: remoteChange.previous?.sections },
      { ...remoteChange.next, slides: remoteChange.next?.sections });
    const count = Object.keys(found).length;
    if (!count) return;
    setChanges((current) => mergeChanges(current, found));
    const revision = remoteChange.revision || {};
    setNotice({ who: revision.source === "agent" ? "Nexus" : (revision.author_name || "Quelqu'un"), count, summary: revision.summary || "" });
    const timer = setTimeout(() => setChanges({}), HIGHLIGHT_MS * 2);
    return () => clearTimeout(timer);
  }, [remoteChange]);

  const sections = useMemo(() => shown?.sections || [], [shown]);
  const numbers = useMemo(() => sectionNumbers(sections), [sections]);
  const section = sections.find((item) => item.id === selection.sectionId) || null;
  const block = section?.blocks?.find((item) => item.id === selection.blockId) || null;
  const openComments = comments.filter((comment) => comment.status === "open");
  const openBySection = openComments.reduce((acc, comment) => ({ ...acc, [comment.slide_id]: (acc[comment.slide_id] || 0) + 1 }), {});
  const title = data?.presentation?.title;

  usePublishNexusPage(data ? {
    description: `Rapport « ${title} »` + (section ? `, section ${numbers[section.id] || ""} « ${section.title} »` : ""),
    extra: {
      deck_document: "rapport client (outils reports__*, méthode Innowide à lire : reports__charte)",
      report_id: id,
      presentation_id: id,
      report_title: title,
      rattachement: data.presentation.scope?.label,
      section_id: section?.id || null,
      section_title: section?.title || null,
      selected_block_id: block?.id || null,
      selected_block_type: block?.type || null,
      open_comments: openComments.length,
      consulted_version: viewing ? viewing.revision.number : null,
      can_edit: editable,
    },
  } : null);

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 py-10 text-center">
        <p className="text-slate-700">{loadError}</p>
        <Link to="/tools/rapports" className="text-sm text-blue-700 hover:underline">Retour aux rapports</Link>
      </div>
    );
  }
  if (!data || !shown) return <div className="py-10 text-center text-sm text-slate-500">Chargement…</div>;

  const presentation = data.presentation;
  const scope = presentation.scope;
  const embeds = (viewing || data).embeds || {};
  const select = (sectionId, blockId = null) => setSelection({ sectionId, blockId });
  const goTo = (sectionId, blockId) => {
    select(sectionId, blockId || null);
    nodes.current[sectionId]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const onMeta = (patch, key) => apply((current) => updateMeta(current, patch), key);
  const onSection = (sectionId, patch, key) => apply((current) => updateSection(current, sectionId, patch), key && `${sectionId}.${key}`);
  const onBlock = (sectionId, blockId, patch, key) => apply((current) => updateBlock(current, sectionId, blockId, patch), key);
  const addSection = (level, annex = false) => {
    const created = newSection(level, annex);
    const after = annex ? sections[sections.length - 1]?.id
      : (selection.sectionId || [...sections].reverse().find((item) => !item.annex)?.id);
    apply((current) => insertSection(current, after, created));
    setTimeout(() => goTo(created.id), 50);
  };
  const deleteSection = (sectionId) => {
    if (!window.confirm("Supprimer cette section et ses blocs ? (Ctrl+Z pour revenir en arrière)")) return;
    apply((current) => removeSection(current, sectionId));
    select(null);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await save();
      const { blob, filename } = await reportApi.pdf(id, viewing?.revision.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setExporting(false);
    }
  };

  const askNexusForComments = () => openNexus(
    `Applique ${openComments.length > 1 ? `les ${openComments.length} commentaires ouverts` : "le commentaire ouvert"} du rapport « ${title} », `
    + `puis marque-${openComments.length > 1 ? "les" : "le"} comme traité${openComments.length > 1 ? "s" : ""} en disant ce que tu as modifié.`,
  );
  const askNexus = () => openNexus(section ? `Dans la section ${numbers[section.id]} « ${section.title} »${block ? ", bloc sélectionné" : ""} du rapport : ` : `Sur le rapport « ${title} » : `);
  const meta = shown.meta;
  const sectionIndex = sections.findIndex((item) => item.id === section?.id);

  return (
    <div className="space-y-4 py-6">
      <div className="space-y-2">
        <BackLink to="/tools/rapports" label="Rapports" />
        <div className="flex flex-col gap-3 min-[1700px]:flex-row min-[1700px]:items-start min-[1700px]:justify-between">
          <div className="min-w-0 flex-1 space-y-1">
            <TitleField value={presentation.title} editable={canEdit} label="Titre du rapport" onCommit={(value) => updateDocument({ title: value })} />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-sm text-slate-500">
              <span className="font-medium text-slate-700">{SCOPE_NOUNS[scope?.type]}</span>
              {scope?.type === "project" ? <Link to={`/project/${scope.id}`} className="hover:text-slate-800 hover:underline">{scope.label}</Link> : <span>{scope?.label}</span>}
              <DriveChip drive={drive} onOpen={() => setPanel("settings")} />
              {!canEdit && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><Lock className="h-3 w-3" /> Lecture seule</span>}
              {archived && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">Archivé</span>}
              <SaveStatus doc={doc} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {canEdit && !archived && (
              <>
                <ToolbarButton onClick={undo} disabled={!canUndo || !editable} title="Annuler (Ctrl+Z)"><Undo2 className="h-4 w-4" /> Annuler</ToolbarButton>
                <ToolbarButton onClick={redo} disabled={!canRedo || !editable} title="Rétablir (Ctrl+Y)"><Redo2 className="h-4 w-4" /> Rétablir</ToolbarButton>
              </>
            )}
            <ToolbarButton onClick={() => setPanel(panel === "revisions" ? null : "revisions")} active={panel === "revisions"} title="Historique des versions">
              <History className="h-4 w-4" /> Versions
            </ToolbarButton>
            <ToolbarButton onClick={() => setPanel(panel === "comments" ? null : "comments")} active={panel === "comments"} title="Commentaires sur les sections, que Nexus peut appliquer">
              <MessageSquare className="h-4 w-4" /> Commentaires
              {openComments.length > 0 && <span className="rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">{openComments.length}</span>}
            </ToolbarButton>
            <ToolbarButton onClick={() => setPanel(panel === "edit" ? null : "edit")} active={panel === "edit"} title="Réglages de la section ou du bloc sélectionné, ajout de blocs">
              <SlidersHorizontal className="h-4 w-4" /> Édition
            </ToolbarButton>
            {editable && <ToolbarButton onClick={askNexus} title="Demander une modification à Nexus"><Sparkles className="h-4 w-4" /> Nexus</ToolbarButton>}
            <ToolbarButton onClick={() => setPanel(panel === "settings" ? null : "settings")} active={panel === "settings"} title="Couverture, sommaire, logo, PDF sur le Drive, modèles">
              <Settings2 className="h-4 w-4" /> Réglages
            </ToolbarButton>
            <ToolbarButton onClick={() => setPanel(panel === "guides" ? null : "guides")} active={panel === "guides"} title="Consignes du client, du programme et du projet, tenues par l'IA">
              <ListChecks className="h-4 w-4" /> Consignes
            </ToolbarButton>
            <ToolbarButton onClick={exportPdf} disabled={exporting} title="Télécharger le PDF (pages A4, sommaire paginé)">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {exporting ? "Préparation du PDF…" : "PDF"}
            </ToolbarButton>
            {canEdit && (
              <ToolbarButton onClick={() => updateDocument({ archived: !archived })} title={archived ? "Désarchiver" : "Archiver"}>
                {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />} {archived ? "Désarchiver" : "Archiver"}
              </ToolbarButton>
            )}
          </div>
        </div>
      </div>

      <SyncBanners doc={doc} noun="Le rapport" />
      {notice && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
          <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4" />
            {notice.who} a modifié {notice.count > 1 ? `${notice.count} sections` : "une section"}{notice.summary ? ` : ${notice.summary}` : "."}</span>
          <button type="button" aria-label="Fermer" className="rounded-full p-1 text-sky-700 hover:bg-sky-100" onClick={() => setNotice(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      <div ref={workspaceRef} className="flex flex-col gap-4 xl:flex-row" style={height ? { height } : undefined}>
        {/* Plan : un clic va à la section ; monter, descendre, supprimer la section ouverte. */}
        <nav aria-label="Plan du rapport" className="shrink-0 space-y-1 xl:h-full xl:w-60 xl:overflow-y-auto xl:pr-1">
          {sections.map((item, index) => (
            <div key={item.id} className={`group rounded-lg ${item.id === section?.id ? "bg-white shadow-sm ring-1 ring-blue-200" : "hover:bg-white"}`}>
              <button type="button" onClick={() => goTo(item.id)} aria-current={item.id === section?.id ? "true" : undefined}
                className={`flex w-full items-baseline gap-2 px-2 py-1.5 text-left text-sm ${item.level === 2 ? "pl-6" : ""} ${item.hidden ? "opacity-40" : ""}`}>
                <span className="shrink-0 font-semibold text-blue-600">{numbers[item.id] || "–"}</span>
                <span className={`min-w-0 flex-1 truncate ${item.level === 2 ? "text-slate-600" : "font-medium text-slate-800"}`}>{item.title || "Sans titre"}</span>
                {openBySection[item.id] > 0 && <span className="rounded-full bg-amber-100 px-1.5 text-[11px] text-amber-800">{openBySection[item.id]}</span>}
                {changes[item.id] && <Sparkles className="h-3 w-3 shrink-0 text-sky-500" />}
              </button>
              {editable && item.id === section?.id && (
                <div className="flex justify-end gap-0.5 px-1 pb-1">
                  <button type="button" title="Monter" disabled={index === 0} onClick={() => apply((c) => moveSection(c, item.id, -1))} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" title="Descendre" disabled={index === sections.length - 1} onClick={() => apply((c) => moveSection(c, item.id, 1))} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button type="button" title="Supprimer la section" onClick={() => deleteSection(item.id)} className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          ))}
          {editable && (
            <div className="flex flex-wrap gap-1 pt-2">
              <button type="button" onClick={() => addSection(1)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-white"><Plus className="h-3 w-3" /> Section</button>
              <button type="button" onClick={() => addSection(2)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-white"><Plus className="h-3 w-3" /> Sous-section</button>
              <button type="button" onClick={() => addSection(1, true)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-white"><Plus className="h-3 w-3" /> Annexe</button>
            </div>
          )}
          {sectionIndex < 0 && sections.length > 0 && <p className="px-2 pt-2 text-xs text-slate-400">Cliquez une section pour la régler.</p>}
        </nav>

        {/* Le rapport, comme imprimé : couverture, sommaire, puis les sections en pages A4. */}
        <main ref={paperRef} className="relative min-w-0 flex-1 overflow-auto rounded-lg bg-slate-200/70 p-4 pt-0 xl:h-full" onClick={() => select(null)}>
          <div className="sticky left-0 top-0 z-20 -mx-4 mb-3 flex justify-end px-4 pt-3" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-0.5 rounded-full border border-slate-200 bg-white/95 px-1 py-0.5 text-xs text-slate-600 shadow-sm">
              <button type="button" title="Dézoomer" aria-label="Dézoomer" disabled={scale <= ZOOMS[0] + 0.01}
                onClick={() => setZoom(nextZoom(scale, -1))} className="rounded-full p-1.5 hover:bg-slate-100 disabled:opacity-30">
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="w-11 text-center tabular-nums">{Math.round(scale * 100)} %</span>
              <button type="button" title="Zoomer" aria-label="Zoomer" disabled={scale >= ZOOMS[ZOOMS.length - 1] - 0.01}
                onClick={() => setZoom(nextZoom(scale, 1))} className="rounded-full p-1.5 hover:bg-slate-100 disabled:opacity-30">
                <ZoomIn className="h-4 w-4" />
              </button>
              <button type="button" title="Ajuster à la largeur" onClick={() => setZoom(null)}
                className={`rounded-full px-2 py-1 ${zoom ? "hover:bg-slate-100" : "bg-slate-900 text-white"}`}>
                Ajuster
              </button>
            </div>
          </div>
          <div style={{ zoom: scale, width: PAGE.width, margin: "0 auto" }}>
          <ReportDocument content={shown} title={presentation.title} clientName={scope?.client_name} embeds={embeds} live
            edit={editable} selection={selection} onSelect={select} highlight={changes}
            onSection={onSection} onBlock={onBlock} onMeta={onMeta} onTitle={canEdit ? (value) => updateDocument({ title: value }) : undefined}
            sectionRef={(sectionId, node) => { nodes.current[sectionId] = node; }} />
          </div>
        </main>

        {panel && (
          <div className="shrink-0 xl:h-full xl:overflow-y-auto">
            {panel === "revisions" && (
              <RevisionsPanel revisions={revisions} canEdit={canEdit && !archived} viewingId={viewing?.revision?.id}
                onView={viewRevision} onRestore={restoreRevision} onLabel={labelRevision} onClose={() => setPanel(null)}
            clientShare={clientShare} />
            )}
            {panel === "comments" && (
              <CommentsPanel proposalId={id} api={reportApi} part="section" comments={comments} refresh={refreshComments}
                slides={sections} pages={numbers} currentSlide={section} selectedBlockId={selection.blockId} canEdit={canEdit}
                userEmployeeId={user?.employee_id} onGo={goTo} onAskNexus={askNexusForComments} onClose={() => setPanel(null)} onError={setMessage} />
            )}
            {panel === "edit" && (
              <aside className="w-full shrink-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:w-72">
                <ReportInspector section={section} block={block} editable={editable}
                  onSection={(patch) => onSection(section.id, patch)}
                  onBlock={(patch) => onBlock(section.id, block.id, patch)}
                  onAddBlock={(created) => { apply((c) => addBlock(c, section.id, created, selection.blockId)); select(section.id, created.id); }}
                  onMoveBlock={(delta) => apply((c) => moveBlock(c, section.id, block.id, delta))}
                  onDuplicateBlock={() => apply((c) => duplicateBlock(c, section.id, block.id))}
                  onRemoveBlock={() => { apply((c) => removeBlock(c, section.id, block.id)); select(section.id); }}
                  onDeselect={() => select(section.id)} />
              </aside>
            )}
            {panel === "settings" && (
              <PresentationSettings id={id} api={reportApi} data={data} meta={meta} editable={editable} onMeta={onMeta} drive={drive}
                onTemplateSaved={(template) => setData((previous) => ({ ...previous, template }))}
                onCategory={(category) => updateDocument({ category }).then(() => drive.refresh())}
                onClientLogo={(logo) => setData((previous) => ({ ...previous, client_logo: logo }))}
                onError={setMessage} onClose={() => setPanel(null)}>
                <Check label="Sommaire paginé après la couverture" checked={meta.toc} disabled={!editable} onChange={(toc) => onMeta({ toc })} />
                <Check label="Mention « Confidentiel » en pied de page" checked={meta.confidential} disabled={!editable}
                  onChange={(confidential) => onMeta({ confidential })} />
              </PresentationSettings>
            )}
            {panel === "guides" && (
              <GuidesPanel id={id} api={reportApi} scope={scope} editable={canEdit} onError={setMessage} onClose={() => setPanel(null)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportEditor;
